import { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';

interface ImagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  title?: string;
  onDownload: () => void;
  isDownloading?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_ZOOM = 2.5;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function getTouchDistance(touches: { length: number; 0?: Touch; 1?: Touch }) {
  if (touches.length < 2 || !touches[0] || !touches[1]) return 0;
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
}

export function ImagePreviewModal({
  isOpen,
  onClose,
  imageUrl,
  title,
  onDownload,
  isDownloading,
  onPrev,
  onNext,
}: ImagePreviewModalProps) {
  const layer = useModalLayer({
    enabled: isOpen,
    onClose,
  });

  const viewportRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isGesturing, setIsGesturing] = useState(false);
  const gestureRef = useRef({
    pinchStartDistance: 0,
    pinchStartScale: 1,
    panStartX: 0,
    panStartY: 0,
    panStartPosX: 0,
    panStartPosY: 0,
    isPanning: false,
    isPinching: false,
    lastTapTime: 0,
  });

  const resetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      }
    },
    [onPrev, onNext],
  );

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    resetZoom();
  }, [imageUrl, resetZoom]);

  useEffect(() => {
    if (!isOpen) {
      resetZoom();
    }
  }, [isOpen, resetZoom]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!isOpen || !viewport) return;

    const preventScrollWhileGesturing = (e: TouchEvent) => {
      if (gestureRef.current.isPinching || gestureRef.current.isPanning) {
        e.preventDefault();
      }
    };

    viewport.addEventListener('touchmove', preventScrollWhileGesturing, { passive: false });
    return () => viewport.removeEventListener('touchmove', preventScrollWhileGesturing);
  }, [isOpen]);

  const toggleZoom = useCallback(() => {
    setScale((current) => {
      if (current > 1) {
        setPosition({ x: 0, y: 0 });
        return 1;
      }
      return DOUBLE_TAP_ZOOM;
    });
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    const delta = -e.deltaY * 0.002;
    setScale((current) => {
      const next = clamp(current + delta * current, MIN_SCALE, MAX_SCALE);
      if (next <= 1) {
        setPosition({ x: 0, y: 0 });
      }
      return next;
    });
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();
      const gesture = gestureRef.current;

      if (e.touches.length === 2) {
        setIsGesturing(true);
        gesture.isPinching = true;
        gesture.isPanning = false;
        gesture.pinchStartDistance = getTouchDistance(e.touches);
        gesture.pinchStartScale = scale;
        return;
      }

      if (e.touches.length === 1 && scale > 1) {
        setIsGesturing(true);
        gesture.isPanning = true;
        gesture.isPinching = false;
        gesture.panStartX = e.touches[0].clientX;
        gesture.panStartY = e.touches[0].clientY;
        gesture.panStartPosX = position.x;
        gesture.panStartPosY = position.y;
        return;
      }

      if (e.touches.length === 1) {
        const now = Date.now();
        if (now - gesture.lastTapTime < 300) {
          toggleZoom();
          gesture.lastTapTime = 0;
        } else {
          gesture.lastTapTime = now;
        }
      }
    },
    [scale, position, toggleZoom],
  );

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
    const gesture = gestureRef.current;

    if (gesture.isPinching && e.touches.length === 2) {
      const distance = getTouchDistance(e.touches);
      if (gesture.pinchStartDistance === 0) return;

      const ratio = distance / gesture.pinchStartDistance;
      const nextScale = clamp(gesture.pinchStartScale * ratio, MIN_SCALE, MAX_SCALE);
      setScale(nextScale);
      if (nextScale <= 1) {
        setPosition({ x: 0, y: 0 });
      }
      return;
    }

    if (gesture.isPanning && e.touches.length === 1) {
      const dx = e.touches[0].clientX - gesture.panStartX;
      const dy = e.touches[0].clientY - gesture.panStartY;
      setPosition({
        x: gesture.panStartPosX + dx,
        y: gesture.panStartPosY + dy,
      });
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    const gesture = gestureRef.current;
    gesture.isPinching = false;
    gesture.isPanning = false;
    setIsGesturing(false);
    setScale((current) => {
      if (current <= 1) {
        setPosition({ x: 0, y: 0 });
      }
      return current;
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (scale <= 1 || e.button !== 0) return;
      e.preventDefault();
      setIsGesturing(true);
      const gesture = gestureRef.current;
      gesture.isPanning = true;
      gesture.panStartX = e.clientX;
      gesture.panStartY = e.clientY;
      gesture.panStartPosX = position.x;
      gesture.panStartPosY = position.y;
    },
    [scale, position],
  );

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const gesture = gestureRef.current;
    if (!gesture.isPanning) return;

    const dx = e.clientX - gesture.panStartX;
    const dy = e.clientY - gesture.panStartY;
    setPosition({
      x: gesture.panStartPosX + dx,
      y: gesture.panStartPosY + dy,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    gestureRef.current.isPanning = false;
    setIsGesturing(false);
  }, []);

  if (!isOpen) return null;

  const isZoomed = scale > 1;

  return createPortal(
    <LayerDepthContext.Provider value={layer.depth}>
      <div
        className="fixed inset-0 bg-black/90 flex flex-col"
        style={layer.style}
        onClick={layer.onClose}
      >
        {/* Top bar — extra top padding on mobile so icons clear the status bar */}
        <div className="flex items-center justify-between px-4 py-3 pt-16 sm:pt-3 flex-shrink-0">
          <p className="text-white/80 text-sm truncate max-w-[60%]">
            {title || 'Preview'}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
              disabled={isDownloading}
              className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
              title="Download"
            >
              {isDownloading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Download className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                layer.onClose();
              }}
              className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Image area */}
        <div className="flex-1 flex items-center justify-center min-h-0 relative px-12">
          {onPrev && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPrev();
              }}
              className="absolute left-2 p-2 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors z-10"
              title="Previous"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}

          <div
            ref={viewportRef}
            className={`flex items-center justify-center w-full h-full overflow-hidden touch-none ${
              isZoomed ? 'cursor-grab active:cursor-grabbing' : ''
            }`}
            onClick={(e) => e.stopPropagation()}
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onDoubleClick={(e) => {
              e.stopPropagation();
              toggleZoom();
            }}
          >
            <img
              src={imageUrl}
              alt={title || 'Image preview'}
              className="max-w-full max-h-full object-contain select-none"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transformOrigin: 'center center',
                transition: isGesturing ? 'none' : 'transform 0.15s ease-out',
              }}
              draggable={false}
            />
          </div>

          {onNext && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              className="absolute right-2 p-2 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors z-10"
              title="Next"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}
        </div>
      </div>
    </LayerDepthContext.Provider>,
    document.body,
  );
}
